import type { MmObjectType } from "../api/mentalModels";
import type { CaptureKind } from "../api/capture";

// "Dive deeper" routing (spec 2026-11-02 §5, 2026-11-04 §2.3). The specific path is the
// recommendation, never forced: alternatives are always shown next to it.
// ClaryNext-BE utils/now/lead.js is the server port; both read src/lib/__tests__/diveDeeper.cases.json.

export type MmScriptId = "fear_shrinker" | "belief_audit" | "decision_detox" | "identity_update";

export interface DeeperItem {
  kind: CaptureKind;
  id: string;
  /** The model type, or for other kinds the thought underneath. */
  mmType?: MmObjectType;
}

export type DeeperRoute =
  | { route: "mm_script"; pathId: MmScriptId }
  | { route: "mm_general" }
  | { route: "need_help" }
  | { route: "breakdown" }
  | { route: "guided_activity" }
  | { route: "project_room" }
  | { route: "project_step" }
  | { route: "trait_panel" }
  | { route: "underneath" }
  | { route: "keep" }
  | { route: "operator"; operator: string }
  | { route: "activity"; refId: string; label: string }
  | { route: "next_step" }
  | { route: "question" };

export interface DeeperChoice {
  recommended: DeeperRoute;
  alternatives: DeeperRoute[];
}

export type DeeperPlan = DeeperChoice;

/** Script ids are the keys of ClaryNext-BE utils/mmRegistry.js SCRIPTS. */
export const SCRIPT_FOR_TYPE: Partial<Record<MmObjectType, MmScriptId>> = {
  fear: "fear_shrinker",
  belief: "belief_audit",
  assumption: "belief_audit",
  decision: "decision_detox",
  self_image: "identity_update",
  value: "identity_update",
};

export function mentalModelChoice(mmType?: MmObjectType): DeeperChoice {
  const script = mmType ? SCRIPT_FOR_TYPE[mmType] : undefined;
  return script
    ? { recommended: { route: "mm_script", pathId: script }, alternatives: [{ route: "mm_general" }] }
    : { recommended: { route: "mm_general" }, alternatives: [] };
}

export function planDeeper(input: { kind: CaptureKind; mmType?: MmObjectType; reflective: boolean }): DeeperPlan {
  const { kind, mmType, reflective } = input;
  if (reflective && (kind === "action" || kind === "insight")) {
    return { recommended: { route: "underneath" }, alternatives: [{ route: "keep" }] };
  }
  if (reflective && kind === "quality") {
    return {
      recommended: { route: "underneath" },
      alternatives: [{ route: "trait_panel" }, { route: "keep" }],
    };
  }
  switch (kind) {
    case "mental_model":
      return mentalModelChoice(mmType);
    case "action":
      return { recommended: { route: "need_help" }, alternatives: [] };
    case "project":
      return { recommended: { route: "project_room" }, alternatives: [] };
    case "quality":
      return { recommended: { route: "trait_panel" }, alternatives: [] };
    case "insight":
    default:
      return { recommended: { route: "underneath" }, alternatives: [{ route: "keep" }] };
  }
}

// Need help's two branches and the project's two ways in are separate cards (spec §2.3).
const EXPAND: Partial<Record<DeeperRoute["route"], DeeperRoute[]>> = {
  need_help: [{ route: "breakdown" }, { route: "guided_activity" }],
  project_room: [{ route: "project_room" }, { route: "project_step" }],
};

/** The one or two main cards for an item, and the planDeeper alternatives that go to "More ways". */
export function mainChoices(
  item: { kind: CaptureKind; mmType?: MmObjectType },
  opts: { reflective: boolean }
): { main: DeeperRoute[]; alternatives: DeeperRoute[] } {
  const plan = planDeeper({ kind: item.kind, mmType: item.mmType, reflective: opts.reflective });
  return {
    main: EXPAND[plan.recommended.route] ?? [plan.recommended],
    alternatives: plan.alternatives.filter((r) => r.route !== "keep"),
  };
}

export function routeKey(r: DeeperRoute): string {
  switch (r.route) {
    case "mm_script":
      return r.pathId;
    case "operator":
      return `op:${r.operator}`;
    default:
      return r.route;
  }
}

export interface RouteSize {
  minutes?: number;
  /** "About 5 min" rather than "2 min". */
  about?: boolean;
  opens?: "mental_models" | "project" | "activity";
}

/** Minutes and where a full page opens, per route key (spec §2.3 table). */
export const ROUTE_SIZE: Record<string, RouteSize> = {
  breakdown: { minutes: 5, about: true },
  guided_activity: { minutes: 10, about: true },
  belief_audit: { minutes: 10, about: true, opens: "mental_models" },
  fear_shrinker: { minutes: 10, about: true, opens: "mental_models" },
  decision_detox: { minutes: 10, about: true, opens: "mental_models" },
  identity_update: { minutes: 10, about: true, opens: "mental_models" },
  mm_general: { opens: "mental_models" },
  project_room: { opens: "project" },
  project_step: { minutes: 2 },
  trait_panel: { minutes: 3, about: true },
  underneath: { minutes: 5, about: true, opens: "mental_models" },
  activity: { opens: "activity" },
  next_step: { minutes: 2 },
  question: {},
};

/** Operators carry their minutes from the session; everything else comes from ROUTE_SIZE. */
export function routeSize(r: DeeperRoute, minutes?: number): RouteSize {
  if (r.route === "operator") return minutes ? { minutes } : {};
  return ROUTE_SIZE[routeKey(r)] ?? {};
}

/** Script step counts come from the session payload (BE counts the routable steps). */
export function routeSteps(r: DeeperRoute, main?: { route: string; pathId?: string; steps?: number }[]): number | undefined {
  if (r.route !== "mm_script" || !main) return undefined;
  return main.find((m) => m.route === "mm_script" && m.pathId === r.pathId)?.steps;
}

export type QuestionKey = "action" | "belief" | "fear" | "decision" | "self_image" | "model" | "project" | "quality" | "insight";

/** The guiding question for the item (`now.dive.question.<key>`, spec §2.3 part 2). */
export function questionKey(item: DeeperItem, reflective: boolean): QuestionKey {
  if (mainChoices(item, { reflective }).main[0]?.route === "underneath") return "insight";
  switch (item.kind) {
    case "mental_model": {
      const script = item.mmType ? SCRIPT_FOR_TYPE[item.mmType] : undefined;
      if (script === "belief_audit") return "belief";
      if (script === "fear_shrinker") return "fear";
      if (script === "decision_detox") return "decision";
      if (script === "identity_update") return "self_image";
      return "model";
    }
    case "action":
      return "action";
    case "project":
      return "project";
    case "quality":
      return "quality";
    default:
      return "insight";
  }
}

/** A server tool candidate ("Short on time?" / "More ways") as a route. */
export function toolRoute(t: { tool: string; operator?: string; refId?: string; label?: string }): DeeperRoute | null {
  if (t.tool === "mm_operator" && t.operator) return { route: "operator", operator: t.operator };
  if (t.tool === "next_step") return { route: "next_step" };
  if (t.tool === "activity" && t.refId) return { route: "activity", refId: t.refId, label: t.label ?? "" };
  return null;
}

export const MAX_MORE_ROWS = 4;
const rowKey = (r: DeeperRoute) => (r.route === "activity" ? `activity:${r.refId}` : routeKey(r));

/** "More ways": planDeeper alternatives, then the session's picks, then "Just think it through" (spec §2.3 part 5). */
export function moreWays(input: { alternatives: DeeperRoute[]; more: DeeperRoute[]; shown: DeeperRoute[] }): DeeperRoute[] {
  const seen = new Set(input.shown.map(rowKey));
  seen.add("question");
  const rows: DeeperRoute[] = [];
  for (const r of [...input.alternatives, ...input.more]) {
    const k = rowKey(r);
    if (seen.has(k) || rows.length >= MAX_MORE_ROWS - 1) continue;
    seen.add(k);
    rows.push(r);
  }
  return [...rows, { route: "question" }];
}
